import { useMemo, useState } from 'react';

import { RyeconClock } from '@dbt-labs/sourdough';

import { ColumnTable } from '@/components/ColumnTable';
import { PageHeading } from '@/components/PageHeading';
import { DetailsSection } from '@/components/SectionWithCard';
import { getNameLinkCell } from '@/components/Table/LinkCell';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useLinkToExplorer } from '@/dbt-explorer';
import { useGroupByEnvironment } from '@/hooks/discovery/group';
import { useUrlProjectParams } from '@/hooks/params';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import {
  INITIAL_PAGE_SIZE,
  ResourceFilterTable,
} from '../../../components/ResourceFilterTable';
import { getResourceTypeHeaderIcon, TextIconPair } from '../Header';

type ResourceParams = {
  uniqueId: ParsedUniqueId;
};

export const GroupView = ({ uniqueId }: ResourceParams) => {
  const { environmentId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();
  const projectScope = useUrlProjectParams();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const [groupQuery] = useGroupByEnvironment({
    environmentId,
    userId,
    uniqueId: uniqueId.raw,
  });

  const pageSize = INITIAL_PAGE_SIZE;
  const [numResultsDisplayed, setNumResultsDisplayed] = useState(INITIAL_PAGE_SIZE);

  const edges = groupQuery.fetching
    ? null
    : groupQuery.data?.environment.definition?.groups?.edges;
  const group = edges ? edges[0]?.node : undefined;
  const models = group?.models;

  const displayedModels = useMemo(
    () =>
      models?.slice(0, numResultsDisplayed).map((model) => ({
        ...model,
        contractEnforced:
          model?.contractEnforced == null
            ? null
            : model.contractEnforced
              ? 'True'
              : 'False',
      })),
    [numResultsDisplayed, models],
  );

  if (groupQuery.error) return <>{groupQuery.error.stack}</>;
  if (!groupQuery.fetching && !group) return <>Group not found</>;

  const isLoadingMore = groupQuery.fetching;
  const isInitialLoad = groupQuery.fetching && groupQuery.data === undefined;

  const updatedAt = undefined;
  const headerIcons: TextIconPair[] = [getResourceTypeHeaderIcon('group')];
  if (updatedAt) {
    headerIcons.push({
      ryecon: RyeconClock,
      text: `Updated at ${updatedAt}`,
    });
  }

  return (
    <>
      <PageHeading className="mb-10">{uniqueId.remainder}</PageHeading>
      <DetailsSection heading="Details">
        <ColumnTable
          tableEntries={[
            { key: 'Owner', data: group?.ownerName },
            { key: 'Owner email', data: group?.ownerEmail },
            { key: 'Owner Github', data: group?.ownerGithub },
            { key: 'Owner Slack', data: group?.ownerSlack },
            { key: 'Description', data: group?.description },
            { key: 'Tags', data: group?.tags?.join(', ') },
          ]}
          isLoading={groupQuery.fetching}
        />
      </DetailsSection>
      <ResourceFilterTable
        pluralizedResultName="models"
        data={displayedModels}
        columns={[
          {
            header: 'Name',
            accessorKey: 'name',
            id: 'name',
            minSize: 60,
            maxSize: 240,
            cell: getNameLinkCell(getDetailsPageLink, projectScope),
          },
          {
            header: 'Contract enforced',
            accessorKey: 'contractEnforced',
          },
          {
            header: 'Database',
            accessorKey: 'database',
          },
          {
            header: 'Schema',
            accessorKey: 'schema',
          },
        ]}
        pageInfo={{
          totalCount: models?.length ?? null,
          endCursor: null,
          hasNextPage: numResultsDisplayed < (models?.length ?? 0),
        }}
        isLoading={isInitialLoad}
        isLoadingMore={isLoadingMore}
        pageSize={pageSize}
        fetchMore={() => setNumResultsDisplayed((numResults) => numResults + pageSize)}
      />
    </>
  );
};
